import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { AssessmentQuestion, ReadingStepComponentProps } from "./types";
import { MazeMultiSelect } from "../../../../components/MazeMultiSelect";
import { buildPromptSegments, type PromptSegment } from "../utils";
import { shuffle } from "../../../../lib/helpers";
import { InstructionAudioButton } from "../../../../components/InstructionAudioButton";

type MazeQuestionItem = AssessmentQuestion & {
  shuffled_options: string[];
};

const getMazePlaceholderId = (question: AssessmentQuestion): string =>
  question.placeholder_id?.trim() || question.question_id;

export class MazePromptPlaceholderError extends Error {
  constructor(questionIds: string[]) {
    super(
      `Maze prompt is missing placeholders for question IDs: ${questionIds.join(
        ", ",
      )}`,
    );
    this.name = "MazePromptPlaceholderError";
  }
}

const boundaryPunctuationPattern = /^[\s\p{P}]+|[\s\p{P}]+$/gu;

export const normalizeMazeFallbackWord = (fallbackWord: string): string =>
  fallbackWord.replace(boundaryPunctuationPattern, "");

export const buildMazePromptSegments = (
  maskedText: string,
  fallbackText: string,
  answerableQuestions: AssessmentQuestion[],
): PromptSegment[] => {
  const displayText = maskedText.trim() ? maskedText : fallbackText;
  const segments = buildPromptSegments(displayText);
  const referencedQuestionIds = new Set<string>();

  for (const segment of segments) {
    if (segment.type === "placeholder") {
      referencedQuestionIds.add(segment.questionId);
    }
  }

  const missingQuestionIds = answerableQuestions
    .filter(
      (question) => !referencedQuestionIds.has(getMazePlaceholderId(question)),
    )
    .map((question) => getMazePlaceholderId(question));

  if (missingQuestionIds.length === 0) {
    return segments;
  }

  throw new MazePromptPlaceholderError(missingQuestionIds);
};

const resolveMazePromptSegments = (
  maskedText: string,
  fallbackText: string,
  answerableQuestions: AssessmentQuestion[],
): { error: MazePromptPlaceholderError | null; segments: PromptSegment[] } => {
  try {
    return {
      error: null,
      segments: buildMazePromptSegments(
        maskedText,
        fallbackText,
        answerableQuestions,
      ),
    };
  } catch (error) {
    if (error instanceof MazePromptPlaceholderError) {
      return { error, segments: [] };
    }
    throw error;
  }
};

export function MazeQuestion({
  assessment,
  isLoading,
  onStepReadyChange,
  onSaveAnswer,
}: ReadingStepComponentProps) {
  const mazeQuestions = useMemo(() => {
    const partQuestions = assessment?.part?.questions ?? [];
    return partQuestions.filter(
      (question) => question.question_type === "maze",
    );
  }, [assessment?.part?.questions]);

  const questionMap = useMemo(() => {
    const entries: Array<[string, MazeQuestionItem]> = [];
    for (const question of mazeQuestions) {
      const questionWithOptions = {
        ...question,
        shuffled_options: shuffle(question.options ?? []),
      };
      entries.push([question.question_id, questionWithOptions]);
      const placeholderId = question.placeholder_id?.trim();
      if (placeholderId) {
        entries.push([placeholderId, questionWithOptions]);
      }
    }
    return new Map<string, MazeQuestionItem>(entries);
  }, [mazeQuestions]);

  const initialSelectedByQuestionId = useMemo(
    () =>
      Object.fromEntries(
        mazeQuestions
          .map((question) => [
            question.question_id,
            assessment?.submissionProgress?.answers_by_question_id?.[
              question.question_id
            ] ?? "",
          ])
          .filter(([, answer]) => Boolean(answer)),
      ),
    [assessment?.submissionProgress?.answers_by_question_id, mazeQuestions],
  );
  const [selectedByQuestionId, setSelectedByQuestionId] = useState<
    Record<string, string>
  >(() => initialSelectedByQuestionId);
  const promptText = assessment?.part?.prompt ?? "";
  const answerableMazeQuestions = useMemo(
    () =>
      mazeQuestions.filter((question) => (question.options ?? []).length > 0),
    [mazeQuestions],
  );
  const hasMazeContent =
    Boolean(promptText.trim()) && answerableMazeQuestions.length > 0;
  const maskedText = assessment?.part?.mazeMaskedPrompt ?? "";
  const promptWordTokens = useMemo(
    () => promptText.match(/\S+/g) ?? [],
    [promptText],
  );
  const promptSegments = useMemo(
    () =>
      resolveMazePromptSegments(
        maskedText,
        promptText,
        answerableMazeQuestions,
      ),
    [answerableMazeQuestions, maskedText, promptText],
  );
  const displaySegments = promptSegments.error
    ? [{ type: "text" as const, value: promptText }]
    : promptSegments.segments;

  const isStepReady = useMemo(
    () =>
      !hasMazeContent ||
      Boolean(promptSegments.error) ||
      answerableMazeQuestions.every((question) =>
        Boolean(selectedByQuestionId[question.question_id]),
      ),
    [
      answerableMazeQuestions,
      hasMazeContent,
      promptSegments.error,
      selectedByQuestionId,
    ],
  );

  useEffect(() => {
    onStepReadyChange?.(isStepReady);
  }, [isStepReady, onStepReadyChange]);

  if (isLoading) {
    return null;
  }

  if (!hasMazeContent) {
    return (
      <Typography>
        Maze content is unavailable for this part. You can continue to the next
        step.
      </Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        data-testid="maze-prompt-panel"
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Typography variant={"body1"}>
            {displaySegments.map((segment, index) => {
              if (segment.type === "text") {
                return <span key={`text-${index}`}>{segment.value}</span>;
              }

              const matchingQuestion = questionMap.get(segment.questionId);
              if (!matchingQuestion) {
                const fallbackWord =
                  segment.tokenIndex >= 0
                    ? (promptWordTokens[segment.tokenIndex] ?? "")
                    : "";
                return (
                  <span key={`fallback-${segment.questionId}-${index}`}>
                    {normalizeMazeFallbackWord(fallbackWord) || segment.raw}
                  </span>
                );
              }

              const options = matchingQuestion.shuffled_options ?? [];
              return (
                <Box
                  key={`select-${matchingQuestion.question_id}-${index}`}
                  component="span"
                  sx={{ mx: 0.5, display: "inline-block" }}
                >
                  <MazeMultiSelect
                    options={options}
                    value={
                      selectedByQuestionId[matchingQuestion.question_id] ?? ""
                    }
                    onChange={(nextValue) => {
                      setSelectedByQuestionId((current) => ({
                        ...current,
                        [matchingQuestion.question_id]: nextValue,
                      }));
                      if (nextValue) {
                        void onSaveAnswer?.(
                          matchingQuestion.question_id,
                          nextValue,
                        );
                      }
                    }}
                  />
                </Box>
              );
            })}
          </Typography>
        </CardContent>
      </Card>
      <Card
        data-testid="maze-instructions-panel"
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            <InstructionAudioButton
              audioUrl={assessment?.mazeInstructionsAudio}
              label={`Play reading maze instruction audio`}
            ></InstructionAudioButton>
            <Typography variant={"body1"} sx={{ fontWeight: 800 }}>
              Instructions:
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"flex-start"}
            sx={{ my: 1, minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              {assessment?.mazeInstructions ? (
                <Typography variant={"body1"}>
                  {assessment?.mazeInstructions}
                </Typography>
              ) : (
                <Stack direction={"column"} spacing={2}>
                  <Typography>
                    <span style={{ fontWeight: 800 }}>Read</span> the paragraph.
                  </Typography>
                  <Typography>
                    For each word group, click the word that makes the sentence
                    correct and fits the text.
                  </Typography>
                  <Typography>When you finish, click Next.</Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
